import type { DateStr } from './dates';

/** Отправители писем о вакансиях: LinkedIn, доски и ATS. */
export const JOB_SENDERS = [
  'jobs-noreply@linkedin.com',
  'messages-noreply@linkedin.com',
  // «X только что отправил(а) вам сообщение» — текста сообщения в письме нет
  'messaging-digest-noreply@linkedin.com',
  'inmail-hit-reply@linkedin.com',
  'hit-reply@linkedin.com',
  'glassdoor.com',
  'infojobs.net',
  'bamboohr.com',
  'greenhouse.io',
  'lever.co',
  'workablemail.com',
  'workable.com',
  'smartrecruiters.com',
  'teamtailor.com',
  'personio.de',
  'personio.com',
  'recruitee.com',
  'ashbyhq.com',
  'myworkday.com',
  // Greenhouse и Recruitee шлют с отдельных почтовых доменов
  'greenhouse-mail.io',
  'recruitee-mailbox.com',
  'applytojob.com',
  'pinpointhq.com',
  'successfactors.com',
  'icims.com',
  'jobvite.com',
  'breezy.hr',
  'factorialhr.com',
  'indeed.com',
  // сообщения работодателей через Indeed приходят с адресов вида …@indeedemail.com
  'indeedemail.com',
];

export const SUBJECT_WORDS = [
  'application',
  'applying',
  'applied',
  'interview',
  'entrevista',
  'candidatura',
  'offer',
  'oferta',
  'proceso',
  'заявка',
  'собеседование',
  'вакансия',
  'интервью',
];

/** Автоматические адреса, которые не считаются контактом-человеком. */
export const NOREPLY_RE =
  /no-?reply|notification|notifications|mailer|jobs-|messages-|hit-reply|@linkedin\.com|glassdoor|infojobs|bamboohr|greenhouse|lever\.co|workable|smartrecruiters|teamtailor|personio|recruitee|ashby|myworkday|icims|jobvite|indeed/i;

/**
 * Признаки отказа в тексте письма. Отказ ATS обычно начинается с благодарности за отклик, а решение —
 * во втором абзаце, за пределами сниппета: такие письма всегда читаются целиком.
 */
export const REJECTION_HINT_RE =
  /\bunfortunately\b|regret to inform|other candidates?|(?:move|moving) forward with (?:a|an|another|other)\b|not (?:been )?selected|decided not to (?:proceed|move)|not to (?:proceed|move forward)|lamentablemente|otros? candidat|к сожалению|другого кандидата|другими кандидатами/i;

/**
 * Тема письма-подтверждения отклика (LinkedIn, ATS): такие письма всегда попадают в трекер,
 * даже если быстрая модель сочла их нерелевантными.
 */
export const APPLIED_SUBJECT_RE =
  /заявка (?:была )?отправлена|ваша заявка .{0,80} отправлена|your application (?:was|has been) (?:sent|submitted|received)|application (?:submitted|received)|we(?: have|'ve)? received your application|thank you for applying|tu (?:solicitud|candidatura) (?:se )?(?:ha )?(?:sido )?(?:enviad|recibid)/i;

/** Фразы отказов и ответов работодателей: в теме письма их может не быть («Thanks for your interest in …»). */
export const BODY_PHRASES = [
  'unfortunately',
  'lamentablemente',
  '"next steps"',
  '"regret to inform"',
  '"other candidates"',
  '"move forward with"',
  '"not to move forward"',
  '"not been selected"',
  '"your interest in"',
  '"otros candidatos"',
  '"другими кандидатами"',
];

const EMAIL_RE = /^[^\s()]+@[^\s()]+$/;

/**
 * Поисковый запрос Gmail — основа как `buildQuery()` в прототипе: письма от LinkedIn, досок и ATS,
 * по словам в теме и фразам в теле, от и к контактам из трекера, плюс дополнительное условие пользователя.
 * «Промоакции» отсекаются только для писем по словам: Gmail кладёт туда и письма ATS (Recruitee),
 * поэтому письма от известных отправителей и контактов ищутся во всех вкладках.
 */
export function buildGmailQuery(since: DateStr, contactEmails: readonly string[], extra = ''): string {
  const emails = [
    ...new Set(contactEmails.map((e) => (e || '').trim().toLowerCase()).filter((e) => EMAIL_RE.test(e))),
  ].slice(0, 40);
  const trusted = [`from:(${JOB_SENDERS.join(' OR ')})`];
  if (emails.length) trusted.push(`from:(${emails.join(' OR ')})`, `to:(${emails.join(' OR ')})`);
  const x = extra.trim();
  if (x) trusted.push(`(${x})`);
  const words = `(-category:promotions (subject:(${SUBJECT_WORDS.join(' OR ')}) OR ${BODY_PHRASES.join(' OR ')}))`;
  return `after:${since.replace(/-/g, '/')} -from:jobalerts-noreply@linkedin.com (${[...trusted, words].join(' OR ')})`;
}

/** Адрес из поля From: «Anna <anna@acme.com>» → «anna@acme.com». */
export function senderEmail(from: string): string {
  const m = from.match(/<([^>]+)>/);
  return (m ? m[1]! : from).trim();
}

/** Источник вакансии по отправителю письма. */
export function sourceFromSender(from: string): 'LinkedIn' | 'Glassdoor' | 'InfoJobs' | 'Indeed' | 'Email' {
  return /linkedin/i.test(from)
    ? 'LinkedIn'
    : /glassdoor/i.test(from)
      ? 'Glassdoor'
      : /infojobs/i.test(from)
        ? 'InfoJobs'
        : /@(?:[\w-]+\.)*indeed(?:email)?\.com\b/i.test(from)
          ? 'Indeed'
          : 'Email';
}

/** Уведомление LinkedIn о личном сообщении (дайджест): текста сообщения в нём нет, только кто написал. */
export const LINKEDIN_DM_RE = /messaging-digest-noreply@linkedin\.com/i;

export interface LinkedInDmNotice {
  name: string;
  /** Заголовок профиля: «Lead IT Recruiter | IT | …». */
  headline: string;
  profileUrl: string;
  /** Ссылка на переписку в LinkedIn. */
  threadUrl: string;
}

const PICTO = /[\p{Extended_Pictographic}\u{FE0F}\u{200D}]/gu;

/**
 * Разбор письма-дайджеста LinkedIn: «Имя (заголовок профиля)», ссылка на профиль и
 * «См. сообщение: <ссылка на переписку>» (русский, английский и испанский интерфейс).
 */
export function parseLinkedInMessageDigest(text: string): LinkedInDmNotice[] {
  const out: LinkedInDmNotice[] = [];
  const re =
    /^[ \t]*([^\n()]+?)[ \t]*\(([^\n]*)\)[ \t]*\r?\n[ \t]*\((https?:\/\/[^\s)]+)\)[\s\S]*?(?:См\. сообщени\S*|View messages?|Ver mensajes?)[^:\n]*:\s*(https?:\/\/\S+)/gim;
  for (const m of text.matchAll(re)) {
    const name = m[1]!.replace(PICTO, '').replace(/\s+/g, ' ').trim();
    if (!name) continue;
    out.push({
      name,
      headline: m[2]!.replace(PICTO, '').replace(/\s+/g, ' ').trim(),
      profileUrl: m[3]!.split('?')[0]!,
      threadUrl: m[4]!.split('?')[0]!,
    });
  }
  return out;
}

/* ---------- обычный IMAP (Mail.ru): тот же отбор без поиска Gmail ---------- */

/** Домены Mail.ru — их ящики читаются через imap.mail.ru. */
export const MAILRU_DOMAINS = ['mail.ru', 'inbox.ru', 'list.ru', 'bk.ru', 'internet.ru', 'xmail.ru'];

/** Почтовый сервис по адресу: ящики Mail.ru — mailru, остальное читается как Gmail (в том числе Workspace). */
export function mailService(email: string | null | undefined): 'gmail' | 'mailru' {
  const domain = (email ?? '').trim().toLowerCase().split('@')[1] ?? '';
  return MAILRU_DOMAINS.includes(domain) ? 'mailru' : 'gmail';
}

/** Заголовки письма для отбора на нашей стороне. folder: promo — «Рассылки» и подобные (как «Промоакции» Gmail). */
export interface MailHeaders {
  from: string;
  to: string[];
  subject: string;
  folder: 'inbox' | 'sent' | 'promo' | 'other';
}

/** Фразы для поиска в тексте сервером (IMAP SEARCH BODY): без кавычек синтаксиса Gmail. */
export const IMAP_BODY_PHRASES = BODY_PHRASES.map((p) => p.replace(/"/g, ''));

const SUBJECT_RE = new RegExp(`(^|[^\\p{L}])(${SUBJECT_WORDS.join('|')})(?=[^\\p{L}]|$)`, 'iu');

/** Отправитель из списка: точный адрес («jobs-noreply@linkedin.com») или домен с поддоменами («greenhouse.io»). */
export function senderListed(from: string, list: readonly string[]): boolean {
  const a = senderEmail(from).toLowerCase();
  return list.some((s) => (s.includes('@') ? a === s : a.endsWith(`@${s}`) || a.endsWith(`.${s}`)));
}

/**
 * То же, что buildGmailQuery, на нашей стороне: письма от LinkedIn, досок и ATS, от и к контактам — всегда;
 * по словам в теме или фразам в тексте (bodyHit — нашёл сервер) — кроме рассылок; подборки LinkedIn — нет.
 */
export function jobMailMatch(contactEmails: readonly string[]) {
  const contacts = new Set(
    contactEmails.map((e) => (e || '').trim().toLowerCase()).filter((e) => EMAIL_RE.test(e)),
  );
  return (h: MailHeaders, bodyHit: boolean): boolean => {
    const from = senderEmail(h.from).toLowerCase();
    if (from === 'jobalerts-noreply@linkedin.com') return false;
    if (senderListed(h.from, JOB_SENDERS) || contacts.has(from)) return true;
    if (h.to.some((t) => contacts.has(senderEmail(t).toLowerCase()))) return true;
    return h.folder !== 'promo' && (bodyHit || SUBJECT_RE.test(h.subject));
  };
}
